/**
 * Payment-Link Link-Tester — Parameter-Modell (Spec 501, Phase 5; Spec 503, Phase 3).
 *
 * Reine Logik der Parameter-Tabelle (Modus A): freie Parameter-Zeilen, `meta_`-
 * Schnell-Chips und die Kollisions-/Duplikat-/`meta_`-Prüfung. Ausgelagert aus der
 * UI, damit die Prüf-Regeln (Handover §5) unit-testbar sind.
 *
 * Spec 503 korrigiert die Prüfung auf **case-sensitiv** (Server-Wahrheit:
 * `cbok ≠ cbOk`, Handover §5) und ergänzt die `meta_`-Regeln.
 */

/** Präfix für Merchant-Metadaten, die bis zur Facade durchkommen. */
export const META_PREFIX = 'meta_';

/**
 * `meta_`-Schnell-Chips für den Google-Ads-Fall (Spec 503 §2). Rohe `utm_*` sind
 * wirkungslos (mitsigniert, vom Body verworfen) — nur `meta_`-Parameter erreichen
 * die Facade (`metadata.utm_source` etc.). Die Chips fügen daher die `meta_`-Form ein.
 */
export const UTM_KEYS = [
  'meta_utm_source',
  'meta_utm_medium',
  'meta_utm_campaign',
] as const;

/** Maximale Zahl an `meta_`-Keys (Handover §5). */
export const META_MAX_KEYS = 10;

/** Kanonisches `meta_`-Byte-Budget (Handover §5). */
export const META_MAX_BYTES = 4096;

/** Eine freie Parameter-Zeile (stabile `id` fuer React-Keys + Fehlerzuordnung). */
export interface FreeParamRow {
  id: string;
  key: string;
  value: string;
}

export type ParamIssueType =
  | 'collisionWithSystem'
  | 'reservedSignatureKey'
  | 'duplicateFreeKey'
  | 'caseMismatchWithSystem'
  | 'metaSuffixMissing';

/** Blockierende Probleme sperren scharfen Bau/Copy. */
const BLOCKING_ISSUE_TYPES: ReadonlySet<ParamIssueType> = new Set([
  'collisionWithSystem',
  'reservedSignatureKey',
  'duplicateFreeKey',
  'metaSuffixMissing',
]);

export interface ParamIssue {
  rowId: string;
  type: ParamIssueType;
}

/** Ein `meta_`-Key mit nicht-leerem Suffix? (`meta_` allein ist ungültig.) */
function hasMetaSuffix(key: string): boolean {
  return key.startsWith(META_PREFIX) && key.length > META_PREFIX.length;
}

/**
 * Findet Probleme freier Parameter (Handover §5, **case-sensitiv**):
 * - `collisionWithSystem`: freier Key == ein System-Parameter-Key (exakt),
 * - `reservedSignatureKey`: freier Key == der Signatur-Parameter-Name (exakt),
 * - `caseMismatchWithSystem`: freier Key unterscheidet sich NUR im Case von einem
 *   System-Key (der häufige camelCase-Tippfehler; weicher Hinweis, blockiert nicht),
 * - `duplicateFreeKey`: derselbe freie Key mehrfach (exakt),
 * - `metaSuffixMissing`: ein `meta_`-Key ohne Suffix (ungültig).
 * Leere Keys werden ignoriert (unfertige Zeile). Verhindert, dass Modus A eine URL
 * erzeugt, die Modus B als ungültig behandelt.
 */
export function findParamIssues(
  systemKeys: string[],
  signatureParamName: string,
  freeParams: FreeParamRow[],
): ParamIssue[] {
  const issues: ParamIssue[] = [];
  const sysSet = new Set(systemKeys); // case-sensitiv
  const sysLower = new Set(systemKeys.map((k) => k.toLowerCase()));
  const sigKey = signatureParamName.trim(); // case-sensitiv
  const seen = new Set<string>();

  for (const row of freeParams) {
    const key = row.key.trim();
    if (key === '') {
      continue;
    }
    if (sysSet.has(key)) {
      issues.push({ rowId: row.id, type: 'collisionWithSystem' });
    } else if (sigKey && key === sigKey) {
      issues.push({ rowId: row.id, type: 'reservedSignatureKey' });
    } else if (sysLower.has(key.toLowerCase())) {
      // Kein exakter Treffer, aber case-insensitiv gleich → wahrscheinlich Tippfehler.
      issues.push({ rowId: row.id, type: 'caseMismatchWithSystem' });
    }
    if (key.startsWith(META_PREFIX) && !hasMetaSuffix(key)) {
      issues.push({ rowId: row.id, type: 'metaSuffixMissing' });
    }
    if (seen.has(key)) {
      issues.push({ rowId: row.id, type: 'duplicateFreeKey' });
    } else {
      seen.add(key);
    }
  }
  return issues;
}

/** Aggregat der `meta_`-Budget-Prüfung (Handover §5). */
export interface MetaBudget {
  /** Zahl gültiger `meta_`-Keys (mit Suffix). */
  keyCount: number;
  /** Summierte UTF-8-Byte-Länge aller `meta_`-Paare in `key=value\n`-Form. */
  bytes: number;
  /** > `META_MAX_KEYS` Keys. */
  tooManyKeys: boolean;
  /** > `META_MAX_BYTES`. */
  budgetExceeded: boolean;
}

/**
 * Prüft das `meta_`-Budget (Handover §5): maximal 10 `meta_`-Keys, kombinierte
 * kanonische Byte-Länge ≤ 4096. Gezählt wird über die **dekodierte kanonische**
 * `key=value\n`-Form, als UTF-8-Bytes (`TextEncoder`) — nicht Zeichenzahl.
 */
export function checkMetaBudget(freeParams: FreeParamRow[]): MetaBudget {
  const encoder = new TextEncoder();
  let keyCount = 0;
  let bytes = 0;
  for (const row of freeParams) {
    const key = row.key.trim();
    if (!hasMetaSuffix(key)) {
      continue;
    }
    keyCount += 1;
    bytes += encoder.encode(`${key}=${row.value}\n`).length;
  }
  return {
    keyCount,
    bytes,
    tooManyKeys: keyCount > META_MAX_KEYS,
    budgetExceeded: bytes > META_MAX_BYTES,
  };
}

/**
 * Fehlende Pflicht-Parameter (Modus A). `cbOk` ist Pflicht, kommt aber vom Nutzer
 * (nicht aus dem Prefill) — der scharfe Bau sperrt, solange ein Pflicht-Parameter
 * fehlt. Vergleich case-sensitiv (Server-Wahrheit).
 */
export function missingRequired(
  presentKeys: string[],
  requiredKeys: readonly string[],
): string[] {
  const present = new Set(presentKeys.map((k) => k.trim()).filter((k) => k !== ''));
  return requiredKeys.filter((r) => !present.has(r));
}

/** Gibt es blockierende Parameter-Probleme? (Copy/Signatur sperren.) */
export function hasParamIssues(issues: ParamIssue[]): boolean {
  return issues.some((i) => BLOCKING_ISSUE_TYPES.has(i.type));
}

/** Das erste Problem einer Zeile (fuer die Inline-Fehlermeldung). */
export function issueForRow(
  issues: ParamIssue[],
  rowId: string,
): ParamIssueType | undefined {
  return issues.find((i) => i.rowId === rowId)?.type;
}
